import { useState, useEffect, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useAuth } from "../lib/AuthContext";
import { trackEvent } from "../lib/analytics";
import useAnonymousSearchTracker from "../lib/useAnonymousSearchTracker";
import AnonymousSignupGate from "../components/AnonymousSignupGate";
import { 
  Copy, 
  Check, 
  MapPin, 
  Phone, 
  Calendar, 
  Buildings,
  User,
  ArrowSquareOut,
  Warning,
  ShieldCheck,
  Trophy,
  UsersThree,
  Stethoscope,
  Clock,
  Star,
  CaretRight,
  Question,
  BookOpen
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { 
  api, 
  getProviderName, 
  getProviderNameOnly,
  getCredentials,
  getPrimarySpecialty, 
  getFullAddress, 
  getEnumerationDate,
  generateGoogleMapsLink,
  getSpecialtyColor,
  getNameSlug,
  getLocation,
  getSpecialtySlug
} from "../lib/api";
import { getSpecialtyContent, PROVIDER_FAQS, ALL_SPECIALTIES, ALL_STATES } from "../lib/seoContent";
import PecosBadge from "../components/PecosBadge";
import OigBadge from "../components/OigBadge";
import ComplianceBar from "../components/ComplianceBar";
import AddToWatchlistBtn from "../components/AddToWatchlistBtn";
import SaveProviderBtn from "../components/SaveProviderBtn";
import AddToMonitoringBtn from "../components/AddToMonitoringBtn";
import { ProviderEnrichment, MedicareActivityCard } from "../components/ProviderEnrichment";
import { ProviderSummary } from "../components/ProviderSummary";
import { breadcrumbSchema } from "../lib/jsonLd";

const ProviderProfile = () => {
  const { npi } = useParams();
  const { user, authFetch } = useAuth();
  const [provider, setProvider] = useState(null);
  const [enriched, setEnriched] = useState(null);
  const [relatedProviders, setRelatedProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);
  const loggedRef = useRef(false);
  const anonTrackedRef = useRef(false);
  const { trackSearch, gateOpen, closeGate } = useAnonymousSearchTracker();

  useEffect(() => {
    // Anonymous visitors: each profile view counts as a search.
    // Track once per NPI navigation; tracker hook shows modal on limit.
    if (!user && !anonTrackedRef.current) {
      anonTrackedRef.current = true;
      trackSearch();
    }

    // Reset state when NPI changes so old provider data doesn't linger on
    // SPA navigation between profiles.
    setProvider(null);
    setEnriched(null);
    setRelatedProviders([]);

    let cancelled = false;

    const fetchEnriched = async (attempt = 1) => {
      try {
        return await api.getProviderEnriched(npi);
      } catch (err) {
        const status = err?.response?.status;
        // Real 404 → provider doesn't exist; surface immediately.
        if (status === 404) throw err;
        // Transient (timeout / 5xx / network) → one silent retry, then bubble up.
        if (attempt < 2) {
          await new Promise(r => setTimeout(r, 800));
          return fetchEnriched(attempt + 1);
        }
        throw err;
      }
    };

    const fetchProvider = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await fetchEnriched();
        if (cancelled) return;
        setProvider(data.result);
        setEnriched(data.enriched);

        // GA4 event — provider profile viewed (high-intent action)
        try {
          const basicMeta = data.result?.basic || {};
          const taxMeta = data.result?.taxonomies?.find(t => t.primary) || data.result?.taxonomies?.[0] || {};
          const locMeta = data.result?.addresses?.find(a => a.address_purpose === "LOCATION") || data.result?.addresses?.[0] || {};
          trackEvent("provider_view", {
            npi,
            entity_type: data.result?.enumeration_type === "NPI-1" ? "individual" : "organization",
            specialty: taxMeta.desc || "",
            state: locMeta.state || "",
            has_pecos: data.enriched?.pecos_enrolled ?? null,
            has_oig: data.enriched?.oig_excluded ?? null,
          });
        } catch { /* analytics must never break the app */ }

        // Log search to user history
        if (user && !loggedRef.current) {
          loggedRef.current = true;
          const basic = data.result?.basic || {};
          const name = data.result?.enumeration_type === "NPI-1"
            ? `${basic.first_name || ""} ${basic.last_name || ""}`.trim()
            : basic.organization_name || "";
          const tax = data.result?.taxonomies?.find(t => t.primary) || data.result?.taxonomies?.[0] || {};
          authFetch(`${window.location.origin}/api/dashboard/log-search`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              npi,
              provider_name: name,
              entity_type: data.result?.enumeration_type || "",
              specialty: tax.desc || "",
            }),
          }).catch(() => {});
        }

        // Fetch related providers for internal linking - prefer city-level
        const primarySpec = getPrimarySpecialty(data.result);
        const loc = data.result?.addresses?.find(a => a.address_purpose === 'LOCATION') || data.result?.addresses?.[0];
        if (primarySpec || loc?.state) {
          try {
            const related = await api.getRelatedProviders(npi, {
              specialty: primarySpec?.split(',')[0]?.trim(),
              state: loc?.state,
              city: loc?.city,
              limit: 6
            });
            if (!cancelled) setRelatedProviders(related.results || []);
          } catch (e) { /* non-critical */ }
        }
      } catch (err) {
        if (cancelled) return;
        console.error("Error fetching provider:", err);
        const status = err?.response?.status;
        // Only surface "not found" for real 404s. Transient failures show a
        // distinct retry-able message so users don't see a misleading
        // "Provider Not Found" when the registry is just slow.
        if (status === 404) {
          setError("not_found");
        } else {
          setError("transient");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    if (npi) {
      fetchProvider();
    }

    return () => { cancelled = true; };
  }, [npi]);

  const handleCopyNpi = async () => {
    try {
      await navigator.clipboard.writeText(npi);
      setCopied(true);
      toast.success("NPI copied to clipboard");
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast.error("Failed to copy NPI");
    }
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="animate-pulse">
          <div className="skeleton h-8 w-48 mb-4 rounded" />
          <div className="skeleton h-12 w-3/4 mb-4 rounded" />
          <div className="skeleton h-6 w-1/2 mb-8 rounded" />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="skeleton h-48 rounded-lg" />
            <div className="skeleton h-48 rounded-lg" />
          </div>
        </div>
      </div>
    );
  }

  if (error || !provider) {
    const isTransient = error === "transient";
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-white rounded-lg border border-slate-200 p-12 text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Warning className="w-8 h-8 text-red-500" />
          </div>
          <h1 className="font-display font-bold text-2xl text-slate-900 mb-2" data-testid="provider-error-title">
            {isTransient ? "We couldn't load this provider right now" : "Provider Not Found"}
          </h1>
          <p className="text-slate-500 mb-6">
            {isTransient
              ? "The CMS registry is taking longer than usual to respond. Please try again in a moment."
              : `The provider with NPI ${npi} could not be found in the registry.`}
          </p>
          <div className="flex gap-3 justify-center">
            {isTransient && (
              <Button
                onClick={() => window.location.reload()}
                className="bg-navy hover:bg-navy/90"
                data-testid="provider-error-retry-btn"
              >
                Try Again
              </Button>
            )}
            <Link to="/">
              <Button
                variant={isTransient ? "outline" : "default"}
                className={isTransient ? "" : "bg-navy hover:bg-navy/90"}
                data-testid="provider-error-search-again-btn"
              >
                Search Again
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const name = getProviderName(provider);
  const nameOnly = getProviderNameOnly(provider);
  const credentials = getCredentials(provider);
  const specialty = getPrimarySpecialty(provider);
  const address = getFullAddress(provider);
  const enumerationDate = getEnumerationDate(provider);
  const entityType = provider.enumeration_type === "NPI-1" ? "Individual" : "Organization";
  const specialtyClass = getSpecialtyColor(specialty);
  const mapsLink = generateGoogleMapsLink(address);
  
  // Use enriched taxonomies if available, fallback to original
  const taxonomies = enriched?.taxonomies || provider.taxonomies || [];

  // Enriched taxonomy plain descriptions for SEO
  const enrichedSpecialties = taxonomies
    .filter(t => t.plain_description)
    .map(t => t.plain_description);
  const primaryEnrichedTax = taxonomies.find(t => t.primary) || taxonomies[0];
  const seoJobTitle = primaryEnrichedTax?.plain_description || specialty || "Healthcare Provider";

  // JSON-LD structured data with enriched context
  const jsonLd = entityType === "Individual" ? {
    "@context": "https://schema.org",
    "@type": "Physician",
    "name": nameOnly,
    ...(credentials && { "honorificSuffix": credentials }),
    "jobTitle": seoJobTitle,
    "identifier": {
      "@type": "PropertyValue",
      "propertyID": "NPI",
      "value": npi
    },
    ...(enrichedSpecialties.length > 0 
      ? { "medicalSpecialty": enrichedSpecialties } 
      : specialty ? { "medicalSpecialty": specialty } : {}),
    ...(address && {
      "address": {
        "@type": "PostalAddress",
        "streetAddress": `${address.line1} ${address.line2}`.trim(),
        "addressLocality": address.city,
        "addressRegion": address.state,
        "postalCode": address.postalCode,
        "addressCountry": "US"
      }
    }),
    ...(address?.phone && { "telephone": address.phone }),
    ...(enriched?.years_registered && {
      "knowsAbout": enrichedSpecialties.length > 0 ? enrichedSpecialties : undefined
    })
  } : {
    "@context": "https://schema.org",
    "@type": "MedicalOrganization",
    "name": nameOnly,
    "identifier": {
      "@type": "PropertyValue",
      "propertyID": "NPI",
      "value": npi
    },
    ...(enrichedSpecialties.length > 0 
      ? { "medicalSpecialty": enrichedSpecialties } 
      : specialty ? { "medicalSpecialty": specialty } : {}),
    ...(address && {
      "address": {
        "@type": "PostalAddress",
        "streetAddress": `${address.line1} ${address.line2}`.trim(),
        "addressLocality": address.city,
        "addressRegion": address.state,
        "postalCode": address.postalCode,
        "addressCountry": "US"
      }
    }),
    ...(address?.phone && { "telephone": address.phone })
  };

  const metaDescription = "NPI " + npi + " — " + nameOnly + ", " + (seoJobTitle) + (address ? " in " + address.city + ", " + address.state : " in the United States") + "." + (enriched?.registered_text ? " " + enriched.registered_text + "." : "") + " Verify NPI number, NPPES credentials, PECOS enrollment, and OIG exclusion status.";

  // SEO content for this specialty
  const specialtySlug = getSpecialtySlug(specialty || '');
  const specialtyContent = getSpecialtyContent(specialtySlug);
  const faqs = PROVIDER_FAQS(nameOnly, specialty, address?.state, npi);

  // FAQ Schema structured data
  const faqJsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  // Find matching state for linking
  const stateInfo = address?.state ? ALL_STATES.find(s => s.code === address.state) : null;

  return (
    <>
      <AnonymousSignupGate open={gateOpen} onClose={closeGate} />
      <Helmet>
        <title>{name + " — NPI Number " + npi + " | NPI Lookup"}</title>
        <meta 
          name="description" 
          content={metaDescription} 
        />
        <script type="application/ld+json">
          {JSON.stringify(jsonLd)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(faqJsonLd)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema([
            { name: "Home", url: "/" },
            ...(specialtySlug && specialtyContent ? [{ name: specialtyContent.title, url: `/specialty/${specialtySlug}` }] : []),
            ...(stateInfo ? [{ name: stateInfo.name, url: specialtySlug ? `/specialty/${specialtySlug}/${stateInfo.code.toLowerCase()}` : `/state/${stateInfo.code}` }] : []),
            { name: nameOnly },
          ]))}
        </script>
      </Helmet>

      <div className="bg-slate-50 min-h-screen">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Breadcrumb: Home > Specialty > State > City > Provider */}
          <nav className="mb-6 text-sm" data-testid="breadcrumb">
            <ol className="flex items-center gap-2 text-slate-500 flex-wrap">
              <li><Link to="/" className="hover:text-teal">Home</Link></li>
              <li>/</li>
              {specialtySlug && specialtyContent ? (
                <>
                  <li><Link to={`/specialty/${specialtySlug}`} className="hover:text-teal">{specialtyContent.title}</Link></li>
                  <li>/</li>
                </>
              ) : (
                <>
                  <li><Link to="/search" className="hover:text-teal">Search</Link></li>
                  <li>/</li>
                </>
              )}
              {stateInfo && (
                <>
                  <li>
                    <Link
                      to={specialtySlug ? `/specialty/${specialtySlug}/${stateInfo.code.toLowerCase()}` : `/state/${stateInfo.code}`}
                      className="hover:text-teal"
                    >
                      {stateInfo.name}
                    </Link>
                  </li>
                  <li>/</li>
                </>
              )}
              {address?.city && stateInfo && (
                <>
                  <li>
                    <Link
                      to={`/state/${stateInfo.code}/${encodeURIComponent(address.city.toLowerCase())}`}
                      className="hover:text-teal"
                    >
                      {address.city}
                    </Link>
                  </li>
                  <li>/</li>
                </>
              )}
              <li className="text-slate-900 font-medium">{nameOnly}</li>
            </ol>
          </nav>

          {/* Header Section */}
          <div className="bg-white rounded-lg border border-slate-200 p-6 sm:p-8 mb-6">
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
              <div className="flex-1">
                {/* Entity type badge */}
                <div className="flex items-center gap-3 mb-4 flex-wrap">
                  <Badge 
                    variant="secondary" 
                    className={`entity-badge ${entityType === 'Individual' ? 'individual' : 'organization'}`}
                    data-testid="entity-type-badge"
                  >
                    {entityType === 'Individual' ? (
                      <><User className="w-3 h-3 mr-1" /> Individual</>
                    ) : (
                      <><Buildings className="w-3 h-3 mr-1" /> Organization</>
                    )}
                  </Badge>
                  
                  {/* NPI Badge */}
                  <div 
                    className="npi-badge flex items-center gap-2 text-lg bg-teal/10 text-teal px-3 py-1 rounded-md cursor-pointer hover:bg-teal/20 transition-colors"
                    onClick={handleCopyNpi}
                    title="Click to copy"
                    data-testid="npi-badge"
                  >
                    <span>NPI {npi}</span>
                    {copied ? (
                      <Check className="w-4 h-4 text-green-500" weight="bold" />
                    ) : (
                      <Copy className="w-4 h-4" />
                    )}
                  </div>

                  {/* Experience badge from enriched data */}
                  {enriched?.experience_level && (
                    <Badge className="bg-amber-100 text-amber-800 border-amber-200">
                      <Star className="w-3 h-3 mr-1" weight="fill" />
                      {enriched.experience_level}
                    </Badge>
                  )}
                </div>

                {/* Provider name */}
                <h1 className="font-display font-bold text-3xl sm:text-4xl text-navy mb-2" data-testid="provider-name">
                  {nameOnly}
                  {credentials && <span className="text-slate-400 font-normal">, {credentials}</span>}
                </h1>

                {/* Primary specialty */}
                <p className="text-xl text-teal font-medium mb-4" data-testid="primary-specialty">
                  {specialty || "Specialty not listed"}
                </p>

                {/* Enriched registration text */}
                {enriched?.registered_text && (
                  <div className="flex items-center gap-2 text-slate-600 mb-2">
                    <Clock className="w-5 h-5 text-slate-400" />
                    <span className="font-medium">{enriched.registered_text}</span>
                  </div>
                )}

                {/* Ranking text */}
                {enriched?.ranking_text && (
                  <div className="flex items-center gap-2 text-amber-700 bg-amber-50 px-3 py-2 rounded-lg mt-3">
                    <Trophy className="w-5 h-5" weight="fill" />
                    <span className="text-sm font-medium">{enriched.ranking_text}</span>
                  </div>
                )}

                {/* DEA Lookup CTA */}
                <div className="flex flex-col sm:flex-row flex-wrap gap-2 mt-4">
                  <Link
                    to={`/dea-npi-lookup?npi=${npi}`}
                    className="inline-flex items-center gap-2 text-sm text-navy hover:text-teal font-medium transition-colors"
                    data-testid="check-dea-link"
                    title={`Check if ${nameOnly} (NPI ${npi}) has a DEA controlled substance registration on file`}
                    aria-label={`Look up DEA number for ${nameOnly} using NPI ${npi}`}
                  >
                    <ShieldCheck className="w-4 h-4" aria-hidden="true" />
                    Check DEA Number
                    <CaretRight className="w-3 h-3" aria-hidden="true" />
                  </Link>
                  <Link
                    to={`/verify?npi=${npi}`}
                    className="inline-flex items-center gap-2 text-sm text-navy hover:text-teal font-medium transition-colors"
                    data-testid="verification-report-link"
                    title={`Generate a printable verification report for ${nameOnly} (NPI ${npi})`}
                  >
                    <Clock className="w-4 h-4" aria-hidden="true" />
                    Verification Report
                    <CaretRight className="w-3 h-3" aria-hidden="true" />
                  </Link>
                  <Link
                    to={`/pecos-lookup/npi/${npi}`}
                    className="inline-flex items-center gap-2 text-sm text-navy hover:text-teal font-medium transition-colors"
                    data-testid="check-pecos-link"
                    title={`Check Medicare PECOS enrollment status for ${nameOnly} (NPI ${npi})`}
                  >
                    <ShieldCheck className="w-4 h-4" aria-hidden="true" />
                    PECOS Status
                    <CaretRight className="w-3 h-3" aria-hidden="true" />
                  </Link>
                  <Link
                    to={`/oig-exclusion-lookup/npi/${npi}`}
                    className="inline-flex items-center gap-2 text-sm text-navy hover:text-teal font-medium transition-colors"
                    data-testid="check-oig-link"
                    title={`Check OIG exclusion status for ${nameOnly} (NPI ${npi})`}
                  >
                    <ShieldCheck className="w-4 h-4" aria-hidden="true" />
                    OIG Exclusion
                    <CaretRight className="w-3 h-3" aria-hidden="true" />
                  </Link>
                  <AddToWatchlistBtn
                    npi={npi}
                    providerName={nameOnly}
                    providerType={provider?.enumeration_type}
                    specialty={specialty}
                  />
                  <SaveProviderBtn
                    npi={npi}
                    providerName={nameOnly}
                    entityType={provider?.enumeration_type}
                    specialty={specialty}
                    state={address?.state || ""}
                  />
                  <AddToMonitoringBtn
                    npi={npi}
                    providerName={nameOnly}
                    specialty={specialty}
                    state={address?.state || ""}
                  />
                </div>
              </div>
            </div>

            {/* Compliance Bar */}
            <div className="mt-4">
              <ComplianceBar npi={npi} />
            </div>
          </div>

          {/* Medicare Activity + Percentile Comparison — placed right after header */}
          <MedicareActivityCard
            npi={npi}
            providerName={nameOnly}
            specialty={specialty}
            state={stateInfo?.name || address?.state || ""}
          />

          {/* Unique Provider Summary — SEO content uniqueness layer */}
          <ProviderSummary
            npi={npi}
            name={name}
            specialty={specialty}
            city={address?.city || ""}
            state={stateInfo?.name || address?.state || ""}
            entityType={provider?.enumeration_type || ""}
            enumerationDate={provider?.basic?.enumeration_date || ""}
          />

          {/* Content Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-6">
              {/* Contact & Address */}
              {address && (
                <div className={`bg-white rounded-lg border border-slate-200 p-6 border-l-4 ${specialtyClass}`}>
                  <h2 className="font-display font-semibold text-lg text-slate-900 mb-4">
                    Contact Information
                  </h2>
                  
                  <div className="space-y-4">
                    {/* Address */}
                    <div className="flex items-start gap-3">
                      <MapPin className="w-5 h-5 text-slate-400 mt-0.5" />
                      <div>
                        <p className="text-slate-900" data-testid="provider-address">
                          {address.line1}
                          {address.line2 && <><br />{address.line2}</>}
                          <br />
                          {address.city}, {address.state} {address.postalCode}
                        </p>
                        <a 
                          href={mapsLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-teal hover:underline text-sm mt-2"
                          data-testid="google-maps-link"
                        >
                          View on Google Maps
                          <ArrowSquareOut className="w-4 h-4" />
                        </a>
                      </div>
                    </div>

                    {/* Phone */}
                    {address.phone && (
                      <div className="flex items-center gap-3">
                        <Phone className="w-5 h-5 text-slate-400" />
                        <a 
                          href={`tel:${address.phone}`} 
                          className="text-slate-900 hover:text-teal"
                          data-testid="provider-phone"
                        >
                          {address.phone}
                        </a>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Taxonomies with enriched descriptions */}
              {taxonomies.length > 0 && (
                <div className="bg-white rounded-lg border border-slate-200 p-6">
                  <h2 className="font-display font-semibold text-lg text-slate-900 mb-4">
                    Specialties & Credentials
                  </h2>
                  
                  <div className="space-y-3" data-testid="taxonomies-list">
                    {taxonomies.map((tax, index) => (
                      <div 
                        key={index}
                        className={`p-4 rounded-lg ${tax.primary ? 'bg-teal/5 border border-teal/20' : 'bg-slate-50'}`}
                      >
                        <div className="flex items-start justify-between">
                          <div>
                            <div className="font-medium text-slate-900 flex items-center flex-wrap gap-1">
                              <span>{tax.plain_description || tax.desc || "Unknown"}</span>
                              {tax.primary && (
                                <Badge className="bg-teal text-white text-xs">
                                  Primary
                                </Badge>
                              )}
                            </div>
                            {tax.plain_name && tax.plain_name !== tax.desc && (
                              <p className="text-sm text-teal mt-1">{tax.plain_name}</p>
                            )}
                            {tax.code && (
                              <p className="text-xs text-slate-400 font-mono mt-1">
                                Taxonomy Code: {tax.code}
                              </p>
                            )}
                            {tax.category && (
                              <p className="text-xs text-slate-500 mt-1">
                                Category: {tax.category}
                              </p>
                            )}
                          </div>
                          {tax.state && (
                            <span className="text-sm text-slate-500">
                              {tax.state}
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Medicare Enrollment (PECOS) - Main Section */}
              <PecosBadge npi={npi} isMainSection />

              {/* OIG Exclusion Check - Main Section */}
              <OigBadge npi={npi} isMainSection />

              {/* Co-located Providers */}
              {enriched?.co_located_providers?.length > 0 && (
                <div className="bg-white rounded-lg border border-slate-200 p-6">
                  <div className="flex items-center gap-2 mb-4">
                    <UsersThree className="w-5 h-5 text-navy" />
                    <h2 className="font-display font-semibold text-lg text-slate-900">
                      Other Providers at This Location
                    </h2>
                  </div>
                  <p className="text-sm text-slate-500 mb-4">
                    {enriched.co_located_count} other healthcare provider{enriched.co_located_count !== 1 ? 's' : ''} practice at the same address
                  </p>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {enriched.co_located_providers.slice(0, 6).map((p, i) => (
                      <Link
                        key={p.npi}
                        to={`/provider/${p.npi}/${getNameSlug(p.name)}`}
                        className="p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
                      >
                        <p className="font-medium text-slate-900 text-sm truncate">{p.name}</p>
                        <p className="text-xs text-teal truncate">{p.specialty}</p>
                      </Link>
                    ))}
                  </div>
                </div>
              )}

              {/* Specialty Peers Nearby */}
              {enriched?.specialty_peers_nearby?.length > 0 && (
                <div className="bg-white rounded-lg border border-slate-200 p-6">
                  <div className="flex items-center gap-2 mb-4">
                    <Stethoscope className="w-5 h-5 text-teal" />
                    <h2 className="font-display font-semibold text-lg text-slate-900">
                      Similar Providers Nearby
                    </h2>
                  </div>
                  <p className="text-sm text-slate-500 mb-4">
                    Other {specialty?.split(' ')[0]?.toLowerCase() || 'healthcare'} providers in the area
                  </p>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {enriched.specialty_peers_nearby.slice(0, 6).map((p, i) => (
                      <Link
                        key={p.npi}
                        to={`/provider/${p.npi}/${getNameSlug(p.name)}`}
                        className="p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
                      >
                        <p className="font-medium text-slate-900 text-sm truncate">{p.name}</p>
                        <p className="text-xs text-slate-500">{p.city}, {p.state}</p>
                      </Link>
                    ))}
                  </div>
                </div>
              )}

              {/* Incorrect Information Link */}
              <div className="bg-slate-100 rounded-lg p-4 flex items-center justify-between">
                <div className="flex items-center gap-2 text-slate-600">
                  <Warning className="w-5 h-5" />
                  <span className="text-sm">Is this information incorrect?</span>
                </div>
                <a
                  href="https://nppes.cms.hhs.gov/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-teal hover:underline font-medium"
                  data-testid="update-nppes-link"
                >
                  Update on NPPES
                </a>
              </div>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Credentialing CTA */}
              <div 
                className="cta-bg rounded-lg overflow-hidden relative"
                data-testid="credentialing-cta"
              >
                <div className="bg-navy/80 p-6">
                  <div className="flex items-center gap-2 mb-3">
                    <ShieldCheck className="w-6 h-6 text-teal" weight="fill" />
                    <span className="text-white font-medium">Premium Feature</span>
                  </div>
                  <h3 className="font-display font-bold text-xl text-white mb-2">
                    Verify this provider for your practice
                  </h3>
                  <p className="text-slate-300 text-sm mb-4">
                    Get a comprehensive credentialing report with license verification, malpractice history, and more.
                  </p>
                  <Button 
                    className="w-full bg-teal hover:bg-teal/90 text-white"
                    data-testid="credentialing-report-button"
                  >
                    Get Credentialing Report
                  </Button>
                </div>
              </div>

              {/* Quick Info Card */}
              <div className="bg-white rounded-lg border border-slate-200 p-6">
                <h3 className="font-display font-semibold text-slate-900 mb-4">
                  Quick Info
                </h3>
                <dl className="space-y-3">
                  <div className="flex justify-between">
                    <dt className="text-slate-500">NPI Number</dt>
                    <dd className="font-mono text-slate-900">{npi}</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-slate-500">Entity Type</dt>
                    <dd className="text-slate-900">{entityType}</dd>
                  </div>
                  {address?.state && (
                    <div className="flex justify-between">
                      <dt className="text-slate-500">State</dt>
                      <dd className="text-slate-900">{address.state}</dd>
                    </div>
                  )}
                  {enriched?.years_registered !== undefined && enriched?.years_registered !== null && (
                    <div className="flex justify-between">
                      <dt className="text-slate-500">Years Registered</dt>
                      <dd className="text-slate-900 font-medium">{enriched.years_registered} years</dd>
                    </div>
                  )}
                  {enriched?.experience_percentile && (
                    <div className="flex justify-between">
                      <dt className="text-slate-500">Experience Rank</dt>
                      <dd className="text-amber-600 font-medium">Top {100 - enriched.experience_percentile}%</dd>
                    </div>
                  )}
                  {enumerationDate && (
                    <div className="flex justify-between">
                      <dt className="text-slate-500">Registered</dt>
                      <dd className="text-slate-900">{enumerationDate}</dd>
                    </div>
                  )}
                </dl>
              </div>

              {/* Stats Card */}
              {enriched?.total_in_specialty_cached && (
                <div className="bg-slate-50 rounded-lg border border-slate-200 p-4">
                  <p className="text-xs text-slate-500 text-center">
                    Based on {enriched.total_in_specialty_cached.toLocaleString()} cached providers in this specialty
                  </p>
                </div>
              )}

              {/* Browse Directory Links */}
              <div className="bg-white rounded-lg border border-slate-200 p-6">
                <h3 className="font-display font-semibold text-slate-900 mb-4">
                  Browse Directory
                </h3>
                <div className="space-y-2">
                  {specialtySlug && specialtyContent && (
                    <Link
                      to={`/specialty/${specialtySlug}`}
                      className="flex items-center justify-between p-2 rounded-lg hover:bg-slate-50 text-sm text-slate-700 transition-colors"
                      data-testid="browse-specialty-link"
                      title={`Browse all ${specialtyContent.title} providers nationwide`}
                    >
                      <span>All {specialtyContent.title} Providers</span>
                      <CaretRight className="w-4 h-4 text-slate-400" aria-hidden="true" />
                    </Link>
                  )}
                  {stateInfo && (
                    <Link
                      to={`/state/${stateInfo.code}`}
                      className="flex items-center justify-between p-2 rounded-lg hover:bg-slate-50 text-sm text-slate-700 transition-colors"
                      data-testid="browse-state-link"
                      title={`Browse all healthcare providers in ${stateInfo.name}`}
                    >
                      <span>Providers in {stateInfo.name}</span>
                      <CaretRight className="w-4 h-4 text-slate-400" aria-hidden="true" />
                    </Link>
                  )}
                  {specialtySlug && stateInfo && (
                    <Link
                      to={`/specialty/${specialtySlug}/${stateInfo.code.toLowerCase()}`}
                      className="flex items-center justify-between p-2 rounded-lg hover:bg-slate-50 text-sm text-slate-700 transition-colors"
                      data-testid="browse-specialty-state-link"
                      title={`Browse ${specialtyContent?.title || specialty} providers in ${stateInfo.name}`}
                    >
                      <span>{specialtyContent?.title || specialty} in {stateInfo.name}</span>
                      <CaretRight className="w-4 h-4 text-slate-400" />
                    </Link>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Related Providers Section - Major SEO internal linking */}
          {relatedProviders.length > 0 && (
            <div className="mt-8" data-testid="related-providers-section">
              <div className="bg-white rounded-lg border border-slate-200 p-6">
                <div className="flex items-start justify-between gap-4 flex-wrap mb-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <Stethoscope className="w-5 h-5 text-teal shrink-0" aria-hidden="true" />
                    <h2 className="font-display font-semibold text-xl text-slate-900">
                      {(() => {
                        const label = specialty ? `Other ${specialty.split(',')[0].trim()} Providers` : 'Related Providers';
                        const suffix = address?.city ? `in ${address.city}, ${address.state}` : stateInfo ? `in ${stateInfo.name}` : '';
                        const heading = suffix ? `${label} ${suffix}` : label;
                        // Link to the programmatic specialty×city page when we have all three bits
                        if (specialtySlug && address?.city && address?.state) {
                          const citySlugLink = address.city.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
                          return (
                            <Link
                              to={`/providers/${specialtySlug}-in-${citySlugLink}-${address.state.toLowerCase()}`}
                              className="hover:text-teal hover:underline transition-colors"
                              data-testid="related-providers-city-heading-link"
                              title={`View all ${specialty ? specialty.split(',')[0].trim() : 'healthcare'} providers in ${address.city}, ${address.state}`}
                              aria-label={`View all ${specialty ? specialty.split(',')[0].trim() + ' ' : ''}providers in ${address.city}, ${address.state}`}
                            >
                              {heading}
                            </Link>
                          );
                        }
                        return heading;
                      })()}
                    </h2>
                  </div>
                  {/* Always-visible top-right city link for crawlability + SEO */}
                  {specialtySlug && address?.city && address?.state && (
                    <Link
                      to={(() => {
                        const citySlugLink = address.city.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
                        return `/providers/${specialtySlug}-in-${citySlugLink}-${address.state.toLowerCase()}`;
                      })()}
                      className="inline-flex items-center gap-1 text-sm text-teal hover:underline font-medium shrink-0 whitespace-nowrap"
                      data-testid="view-all-city-link-top"
                      title={`View all ${specialty ? specialty.split(',')[0].trim() : ''} providers in ${address.city}, ${address.state}`}
                      aria-label={`View all ${specialty ? specialty.split(',')[0].trim() + ' ' : ''}providers in ${address.city}, ${address.state}`}
                    >
                      View all in {address.city}, {address.state} &rarr;
                    </Link>
                  )}
                </div>
                <p className="text-sm text-slate-500 mb-6">
                  Browse similar healthcare providers {address?.city ? `in ${address.city}` : stateInfo ? `in ${stateInfo.name}` : 'near this location'}
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {relatedProviders.map((rp) => {
                    const rpBasic = rp.basic || {};
                    const rpName = rp.enumeration_type === 'NPI-1'
                      ? `${rpBasic.first_name || ''} ${rpBasic.last_name || ''}`.trim()
                      : rpBasic.organization_name || '';
                    const rpCred = rpBasic.credential ? `, ${rpBasic.credential}` : '';
                    const rpSpecialty = rp.taxonomies?.find(t => t.primary)?.desc || rp.taxonomies?.[0]?.desc || '';
                    const rpAddr = rp.addresses?.find(a => a.address_purpose === 'LOCATION') || rp.addresses?.[0];
                    const rpSlug = getNameSlug(rpName);
                    
                    return (
                      <Link
                        key={rp.number}
                        to={`/provider/${rp.number}/${rpSlug}`}
                        className="group p-4 bg-slate-50 rounded-lg hover:bg-white hover:shadow-md border border-transparent hover:border-slate-200 transition-all"
                        data-testid={`related-provider-${rp.number}`}
                        title={`View ${rpName}'s profile — ${rpSpecialty} in ${rpAddr?.city || ''}, ${rpAddr?.state || ''}`}
                      >
                        <p className="font-medium text-slate-900 group-hover:text-navy truncate">
                          {rpName}{rpCred}
                        </p>
                        <p className="text-sm text-teal truncate mt-1">{rpSpecialty}</p>
                        {rpAddr && (
                          <p className="text-xs text-slate-500 mt-1">
                            {rpAddr.city}, {rpAddr.state}
                          </p>
                        )}
                        <p className="text-xs font-mono text-slate-400 mt-2">NPI: {rp.number}</p>
                      </Link>
                    );
                  })}
                </div>
                {specialtySlug && stateInfo && (
                  <div className="mt-4 flex flex-col sm:flex-row items-center justify-center gap-3">
                    {address?.city && (
                      <Link
                        to={`/providers/${specialtySlug}-in-${address.city.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '')}-${(address.state || stateInfo.code).toLowerCase()}`}
                        className="text-sm text-teal hover:underline font-medium"
                        data-testid="view-all-city-link"
                      >
                        View all {specialtyContent?.title || specialty} providers in {address.city} &rarr;
                      </Link>
                    )}
                    <Link
                      to={`/specialty/${specialtySlug}/${stateInfo.code.toLowerCase()}`}
                      className="text-sm text-teal hover:underline font-medium"
                    >
                      View all {specialtyContent?.title || specialty} providers in {stateInfo.name} &rarr;
                    </Link>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* About This Specialty - SEO educational content */}
          {specialtyContent && (
            <div className="mt-8" data-testid="about-specialty-section">
              <div className="bg-white rounded-lg border border-slate-200 p-6">
                <div className="flex items-center gap-2 mb-4">
                  <BookOpen className="w-5 h-5 text-navy" />
                  <h2 className="font-display font-semibold text-xl text-slate-900">
                    What is a {specialtyContent.title} Specialist?
                  </h2>
                </div>
                <div className="prose prose-slate max-w-none">
                  <p className="text-slate-600 leading-relaxed mb-4">
                    {specialtyContent.description}
                  </p>
                  <h3 className="font-display font-semibold text-base text-slate-900 mt-6 mb-2">
                    What Does a {specialtyContent.title} Specialist Do?
                  </h3>
                  <p className="text-slate-600 leading-relaxed mb-4">
                    {specialtyContent.whatTheyDo}
                  </p>
                  <h3 className="font-display font-semibold text-base text-slate-900 mt-6 mb-2">
                    When Should You See a {specialtyContent.title} Specialist?
                  </h3>
                  <p className="text-slate-600 leading-relaxed mb-4">
                    {specialtyContent.whenToSee}
                  </p>
                  <h3 className="font-display font-semibold text-base text-slate-900 mt-6 mb-2">
                    Education & Training
                  </h3>
                  <p className="text-slate-600 leading-relaxed">
                    {specialtyContent.education}
                  </p>
                </div>

                {/* Related Specialties Links */}
                {specialtyContent.relatedSpecialties?.length > 0 && (
                  <div className="mt-6 pt-6 border-t border-slate-200">
                    <h3 className="text-sm font-medium text-slate-700 mb-3">Related Specialties</h3>
                    <div className="flex flex-wrap gap-2">
                      {specialtyContent.relatedSpecialties.map(rs => {
                        const rsData = ALL_SPECIALTIES.find(s => s.slug === rs);
                        return rsData ? (
                          <Link
                            key={rs}
                            to={`/specialty/${rs}`}
                            className="text-sm px-3 py-1.5 bg-slate-100 rounded-lg hover:bg-teal/10 hover:text-teal text-slate-600 transition-colors"
                          >
                            {rsData.name}
                          </Link>
                        ) : null;
                      })}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Enrichment Sections — CMS Open Payments, Medicare, Affiliations, Identifiers */}
          <ProviderEnrichment npi={npi} />

          {/* FAQ Section with Schema markup */}
          <div className="mt-8 mb-8" data-testid="faq-section">
            <div className="bg-white rounded-lg border border-slate-200 p-6">
              <div className="flex items-center gap-2 mb-6">
                <Question className="w-5 h-5 text-navy" weight="bold" />
                <h2 className="font-display font-semibold text-xl text-slate-900">
                  Frequently Asked Questions
                </h2>
              </div>
              <div className="space-y-4">
                {faqs.map((faq, i) => (
                  <details
                    key={i}
                    className="group border border-slate-200 rounded-lg"
                    data-testid={`faq-item-${i}`}
                  >
                    <summary className="flex items-center justify-between p-4 cursor-pointer select-none hover:bg-slate-50 transition-colors rounded-lg">
                      <span className="font-medium text-slate-900 text-sm pr-4">{faq.question}</span>
                      <CaretRight className="w-4 h-4 text-slate-400 group-open:rotate-90 transition-transform flex-shrink-0" />
                    </summary>
                    <div className="px-4 pb-4 text-sm text-slate-600 leading-relaxed">
                      {faq.answer}
                    </div>
                  </details>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ProviderProfile;
